import { default as React, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Portal from '@mui/material/Portal';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
// Layout Grid and action buttons use @material-ui/core (v4), the same as
// EntityPage.tsx and MicroappAboutCard. The v5 Grid loses its item padding
// inside the v4 entity layout (cards touch the sidebar and each other) and the
// v5 Button loses its theme font.
import { Button as ActionButton, Grid } from '@material-ui/core';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import LaunchIcon from '@mui/icons-material/Launch';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import { EntityLayout } from '@backstage/plugin-catalog';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { EntityArgoCDOverviewCard } from '@roadiehq/backstage-plugin-argo-cd';
import {
  useApi,
  alertApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { ComponentAboutCard } from '../ComponentAboutCard';
import {
  useCurrentUserEmail,
  useSkyStageAdminEmails,
  isUserAuthorizedForEntity,
} from '../microapps/hooks';
import { BackupsTab } from '../microapps/BackupsTab';
import { PodLogsModal } from '../microapps/PodLogsModal';
import { PublicAccessTab } from './PublicAccessTab';
import { PublicAppConfigCard } from './PublicAppConfigCard';
import { PublicAppExpiryBanner } from './PublicAppExpiry';
import { usePublicAppExpiry } from './publicAppExpiryApi';
import { buildPublicAppRedeployFormData } from './publicAppRedeployForm';

const ANNOTATION_OWNER_EMAILS = 'microapp/owners';
const ANNOTATION_CIAM_GROUP_ID = 'skystage.io/ciam-group-id';
const ANNOTATION_ARGOCD_APP = 'argocd/app-name';
const ANNOTATION_HOSTED_APP_URL = 'skystage.io/hosted-app-url';
const ANNOTATION_PERSISTENT_STORAGE = 'microapp/persistentStorage';

const DELETE_STEPS = [
  'Removing manifests from Argo repo…',
  'Deleting ArgoCD application and Kubernetes resources…',
  'Removing CIAM access group…',
  'Deleting container images from Artifact Registry…',
  'Unregistering from SkyStage catalog…',
];

/**
 * About card: Edit Metadata is disabled because the entity definition lives in
 * the argo repo (backstage.yaml) and edits would be overwritten. Same treatment
 * as internal apps (MicroappAboutCard).
 */
const aboutCardSx: SxProps<Theme> = {
  '& [title="Edit Metadata"]': {
    pointerEvents: 'none',
    opacity: 0.38,
    cursor: 'not-allowed',
  },
};

/**
 * Action buttons below the About card, laid out like MicroappAboutCard:
 * Launch + Redeploy side by side, Manage Owners full width underneath.
 * Buttons use the theme font and keep their label on one line.
 */
const aboutCardActionsSx: SxProps<Theme> = {
  mt: 2,
  width: '100%',
  '& a, & button': {
    fontFamily: theme => theme.typography.fontFamily,
    whiteSpace: 'nowrap',
  },
};

const getErrorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : 'Unexpected error';

const readErrorResponse = async (response: Response): Promise<string> => {
  const body = (await response.json().catch(() => ({}))) as {
    error?: string;
  };
  return body.error ?? `Server error ${response.status}`;
};

function useIsPublicAppOwner(): boolean {
  const { entity } = useEntity();
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();
  return isUserAuthorizedForEntity(entity, currentUserEmail, adminEmails);
}

/** Banner shown on the Overview tab when CIAM provisioning failed at deploy time. */
const CiamFailureBanner = () => {
  const { entity } = useEntity();
  const isOwner = useIsPublicAppOwner();
  const ciamGroupId =
    entity.metadata.annotations?.[ANNOTATION_CIAM_GROUP_ID] ?? '';

  if (ciamGroupId) return null;

  return (
    <Grid item xs={12}>
      <Alert severity="error">
        <strong>
          Access broken — CIAM provisioning failed during deployment.
        </strong>{' '}
        The WellSky Identity access group was not created, so{' '}
        <strong>no one can currently reach this app</strong>.
        {isOwner
          ? ' Open the Access tab, add the required users and groups, then click Save Changes to recover.'
          : ' Contact the app owner to fix access via the Access tab.'}
      </Alert>
    </Grid>
  );
};

/** Full-screen overlay shown while deletion is running. */
const DeletingOverlay = ({ appName }: { appName: string }) => (
  <Portal>
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
      }}
    >
      <CircularProgress size={56} sx={{ color: '#fff' }} />
      <Typography variant="h6" sx={{ color: '#fff' }}>
        Deleting <strong>{appName}</strong>…
      </Typography>
      <Box sx={{ textAlign: 'left', color: 'rgba(255,255,255,0.85)' }}>
        {DELETE_STEPS.map(step => (
          <Typography key={step} variant="body2" sx={{ mb: 0.5 }}>
            • {step}
          </Typography>
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
        This may take up to 2 minutes. Please wait…
      </Typography>
    </Box>
  </Portal>
);

/** Dialog for adding/removing owner emails. */
const ManageOwnersDialog = ({
  open,
  appName,
  initialOwnerEmails,
  onClose,
  onSave,
  saving,
  saveError,
}: {
  open: boolean;
  appName: string;
  initialOwnerEmails: string[];
  onClose: () => void;
  onSave: (emails: string[]) => void;
  saving: boolean;
  saveError: string | null;
}) => {
  const [emails, setEmails] = useState<string[]>(initialOwnerEmails);
  const [inputValue, setInputValue] = useState('');

  const handleOpen = () => {
    setEmails(initialOwnerEmails);
    setInputValue('');
  };

  const handleAddEmail = () => {
    const trimmed = inputValue.trim().toLowerCase();
    if (trimmed && !emails.includes(trimmed)) {
      setEmails(prev => [...prev, trimmed]);
    }
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleRemove = (email: string) => {
    setEmails(prev => prev.filter(e => e !== email));
  };

  React.useEffect(() => {
    if (open) handleOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Owners — {appName}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Add or remove owner emails. Owners can redeploy, delete, and manage
          this app. Press <strong>Enter</strong> or <strong>,</strong> after
          each email to add it.
        </DialogContentText>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Add owner email"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="user@wellsky.com"
          />
          <Button
            variant="outlined"
            onClick={handleAddEmail}
            disabled={!inputValue.trim()}
          >
            Add
          </Button>
        </Box>
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: 32 }}
        >
          {emails.map(email => (
            <Chip
              key={email}
              label={email}
              size="small"
              onDelete={() => handleRemove(email)}
            />
          ))}
        </Box>
        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(emails)}
          disabled={saving || emails.length === 0}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Confirmation dialog that requires typing the app name before deletion. */
const DeleteAppDialog = ({
  open,
  appName,
  hasPersistentStorage,
  onClose,
  onConfirm,
  error,
}: {
  open: boolean;
  appName: string;
  /** IDP-1277: warn that /data and its backups are deleted too. */
  hasPersistentStorage: boolean;
  onClose: () => void;
  onConfirm: () => void;
  error: string | null;
}) => {
  const [typed, setTyped] = useState('');

  const handleClose = () => {
    setTyped('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main' }}>Delete Public App</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This will permanently delete <strong>{appName}</strong>:
        </DialogContentText>
        <DialogContentText component="ul" sx={{ mb: 2, pl: 2 }}>
          <li>Kubernetes manifests removed from the Argo repo</li>
          <li>ArgoCD application deleted (foreground cascade)</li>
          <li>CIAM access group deleted from WellSky Identity</li>
          <li>Container images deleted from Artifact Registry</li>
          <li>Catalog entry removed from SkyStage</li>
          {hasPersistentStorage && (
            <li>
              <strong>
                Persistent storage (/data) and all of its backups permanently
                deleted
              </strong>
            </li>
          )}
        </DialogContentText>
        <DialogContentText sx={{ mb: 1 }}>
          Type <strong>{appName}</strong> to confirm:
        </DialogContentText>
        <TextField
          fullWidth
          size="small"
          value={typed}
          onChange={e => setTyped(e.target.value)}
          placeholder={appName}
          autoComplete="off"
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setTyped('');
            onConfirm();
          }}
          disabled={typed !== appName}
        >
          Delete App
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const PublicAppEntityPage = () => {
  const { entity } = useEntity();
  const isOwner = useIsPublicAppOwner();
  const alertApi = useApi(alertApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isRedeploying, setIsRedeploying] = useState(false);
  const [managingOwners, setManagingOwners] = useState(false);
  const [savingOwners, setSavingOwners] = useState(false);
  const [saveOwnersError, setSaveOwnersError] = useState<string | null>(null);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  // IDP-1300: expiry date + Extend.
  const {
    items: expiries,
    expiryDays,
    maxExpiryDays,
    extend: extendExpiry,
  } = usePublicAppExpiry();

  const annotations = entity.metadata.annotations ?? {};
  const appName = entity.metadata.name;
  const entityKind = entity.kind;
  const entityNamespace = entity.metadata.namespace ?? 'default';
  const hostedAppUrl = annotations[ANNOTATION_HOSTED_APP_URL] ?? '';
  const argoAppName = annotations[ANNOTATION_ARGOCD_APP] ?? '';
  const currentOwnerEmails = (annotations[ANNOTATION_OWNER_EMAILS] ?? '')
    .split(',')
    .map((e: string) => e.trim().toLowerCase())
    .filter(Boolean);
  // IDP-1277: persistent storage — drives the Backups tab and delete warning.
  const hasPersistentStorage =
    annotations[ANNOTATION_PERSISTENT_STORAGE] === 'true';

  const handleSaveOwners = async (emails: string[]) => {
    setSavingOwners(true);
    setSaveOwnersError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('microapps-api');
      const response = await fetchApi.fetch(
        `${baseUrl}/update-public-app-owners`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ appName, ownerEmails: emails }),
        },
      );
      if (!response.ok) {
        throw new Error(await readErrorResponse(response));
      }
      setManagingOwners(false);
      // Refresh the entity so the new owner list and owner-only buttons show
      // without a manual sync. Same pattern as ManageOwnersButton.
      try {
        await catalogApi.refreshEntity(stringifyEntityRef(entity));
        window.location.reload();
      } catch {
        // The backend already triggered a refresh; a manual reload picks it up.
      }
    } catch (err: unknown) {
      setSaveOwnersError(getErrorMessage(err));
    } finally {
      setSavingOwners(false);
    }
  };

  const handleRedeploy = async () => {
    setIsRedeploying(true);
    try {
      // Current pub-{app} members, so the Individual Users picker on the
      // redeploy form starts from the real access list (and removals can be
      // applied). If the lookup fails the form still opens — the backend then
      // only adds access and never removes anyone.
      let currentUsers: string[] | undefined;
      let allowedGroups: string[] | undefined;
      try {
        const baseUrl = await discoveryApi.getBaseUrl('microapps-api');
        const annotationGroups = (
          (entity.metadata.annotations ?? {})[
            'skystage.io/allowed-group-names'
          ] ?? ''
        )
          .split(',')
          .map((g: string) => g.trim())
          .filter(Boolean);
        allowedGroups = annotationGroups;
        const groupsParam = annotationGroups.length
          ? `?groups=${encodeURIComponent(annotationGroups.join(','))}`
          : '';
        const response = await fetchApi.fetch(
          `${baseUrl}/ciam/apps/${encodeURIComponent(
            appName,
          )}/access${groupsParam}`,
        );
        if (response.ok) {
          const body = (await response.json()) as {
            users?: unknown;
            individualUsers?: unknown;
            allowedGroups?: unknown;
          };
          // Prefer individualUsers — group-derived members are already excluded.
          let list: unknown[];
          if (Array.isArray(body.individualUsers)) {
            list = body.individualUsers;
          } else if (Array.isArray(body.users)) {
            list = body.users;
          } else {
            list = [];
          }
          currentUsers = list.filter(
            (u): u is string => typeof u === 'string' && u.length > 0,
          );
          // Use groups from DB (via allowedGroups) when available — avoids the
          // annotation lag where the catalog hasn't re-indexed the argo commit yet.
          if (
            Array.isArray(body.allowedGroups) &&
            body.allowedGroups.length > 0
          ) {
            allowedGroups = (body.allowedGroups as unknown[]).filter(
              (g): g is string => typeof g === 'string' && g.length > 0,
            );
          }
        }
      } catch {
        currentUsers = undefined;
      }
      const formData = buildPublicAppRedeployFormData(
        entity,
        currentUsers,
        allowedGroups,
      );
      if (!formData) return;
      const templateUrl = `/create/templates/default/public-app-hosting?formData=${encodeURIComponent(
        JSON.stringify(formData),
      )}`;
      sessionStorage.setItem(`redeployed-${entity.metadata.name}`, '1');
      navigate(templateUrl);
    } finally {
      setIsRedeploying(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDialogOpen(false);
    setDeleting(true);
    setDeleteError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('microapps-api');
      const response = await fetchApi.fetch(
        `${baseUrl}/unregister-public-app`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ appName, entityKind, entityNamespace }),
        },
      );
      if (!response.ok) {
        throw new Error(await readErrorResponse(response));
      }
      const result = await response.json().catch(() => ({}));
      if (
        (result as { catalogCleanupWarning?: string }).catalogCleanupWarning
      ) {
        // Non-fatal: app deleted but catalog entity needs manual cleanup.
        // Show the warning and still navigate — entity will disappear once
        // the catalog re-ingests and finds the GitHub files gone.
        alertApi.post({
          message: (result as { catalogCleanupWarning: string })
            .catalogCleanupWarning,
          severity: 'warning',
          display: 'permanent',
        });
      }
      // All cleanup complete — navigate to the public apps catalog listing
      navigate('/public-apps');
    } catch (err: unknown) {
      setDeleting(false);
      setDeleteError(getErrorMessage(err));
      setDialogOpen(true);
    }
  };

  const showLaunch = !!hostedAppUrl;

  return (
    <>
      {deleting && <DeletingOverlay appName={appName} />}

      <EntityLayout>
        <EntityLayout.Route path="/" title="Overview">
          <Grid container spacing={3}>
            <CiamFailureBanner />
            <PublicAppExpiryBanner
              expiry={expiries.get(appName)}
              expiryDays={expiryDays}
              maxExpiryDays={maxExpiryDays}
              canExtend={isOwner}
              onExtend={() => extendExpiry(appName)}
            />
            <Grid item md={6} xs={12}>
              <Box sx={aboutCardSx}>
                <ComponentAboutCard />
              </Box>

              {(showLaunch || isOwner) && (
                <Box sx={aboutCardActionsSx}>
                  <Grid container spacing={1}>
                    {showLaunch && (
                      <Grid item xs={12} sm={6}>
                        <ActionButton
                          variant="contained"
                          color="primary"
                          size="medium"
                          startIcon={<LaunchIcon />}
                          href={hostedAppUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          fullWidth
                        >
                          Launch
                        </ActionButton>
                      </Grid>
                    )}
                    {isOwner && (
                      <Grid item xs={12} sm={6}>
                        <ActionButton
                          variant="contained"
                          size="medium"
                          startIcon={
                            isRedeploying ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <RefreshIcon />
                            )
                          }
                          onClick={handleRedeploy}
                          disabled={isRedeploying}
                          fullWidth
                        >
                          {isRedeploying ? 'Loading…' : 'Redeploy'}
                        </ActionButton>
                      </Grid>
                    )}
                    {isOwner && argoAppName && (
                      <Grid item xs={12}>
                        <ActionButton
                          variant="outlined"
                          size="medium"
                          startIcon={<DescriptionIcon />}
                          onClick={() => setLogsModalOpen(true)}
                          fullWidth
                        >
                          View Pod Logs
                        </ActionButton>
                      </Grid>
                    )}
                    {isOwner && (
                      <Grid item xs={12}>
                        <ActionButton
                          variant="outlined"
                          size="medium"
                          startIcon={<PeopleIcon />}
                          onClick={() => {
                            setSaveOwnersError(null);
                            setManagingOwners(true);
                          }}
                          fullWidth
                        >
                          Manage Owners
                        </ActionButton>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <PublicAppConfigCard
                isOwner={isOwner}
                onDeleteClick={() => {
                  setDeleteError(null);
                  setDialogOpen(true);
                }}
              />
            </Grid>
            {argoAppName && (
              <Grid item md={6} xs={12}>
                <EntityArgoCDOverviewCard />
              </Grid>
            )}
          </Grid>
        </EntityLayout.Route>

        <EntityLayout.Route path="/access" title="Access" if={() => isOwner}>
          <PublicAccessTab />
        </EntityLayout.Route>

        {/* IDP-1277: same Backups tab as internal apps — the backup API
            resolves the public cluster / namespace from the entity type. */}
        <EntityLayout.Route
          path="/backups"
          title="Backups"
          if={() => isOwner && hasPersistentStorage}
        >
          <BackupsTab />
        </EntityLayout.Route>
      </EntityLayout>

      <ManageOwnersDialog
        open={managingOwners}
        appName={appName}
        initialOwnerEmails={currentOwnerEmails}
        onClose={() => setManagingOwners(false)}
        onSave={handleSaveOwners}
        saving={savingOwners}
        saveError={saveOwnersError}
      />

      <DeleteAppDialog
        open={dialogOpen}
        appName={appName}
        hasPersistentStorage={hasPersistentStorage}
        onClose={() => {
          setDialogOpen(false);
          setDeleteError(null);
        }}
        onConfirm={handleDeleteConfirm}
        error={deleteError}
      />

      {argoAppName && (
        <PodLogsModal
          open={logsModalOpen}
          onClose={() => setLogsModalOpen(false)}
          appName={appName}
          argoCdAppName={argoAppName}
          namespace={`pub-${appName}-ns`}
        />
      )}
    </>
  );
};
